import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdJobs } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Job } from '~hooks/useJob/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

interface UseDiscardedJobsQueryProps {
  companyId?: string;
  cursor?: string;
  direction?: PaginationLink;
  endDate?: string;
  startDate?: string;
}

/**
 * Soft-deleted jobs for the Settings > Deleted Records page.
 *
 * The date range filters `jobs.job_start_at`, not deletion time: `discarded_at` is not
 * exposed on Job-Read.
 */
export default function useDiscardedJobsQuery({
  companyId,
  cursor,
  direction,
  endDate,
  startDate,
}: UseDiscardedJobsQueryProps) {
  const query = useQuery({
    queryKey: ['discardedJobs', companyId, direction, cursor, startDate, endDate],
    queryFn: companyId
      ? async ({ signal }) => {
          try {
            const response = await getV1CompaniesCompanyIdJobs({
              path: { 'company-id': companyId },
              query: {
                'filter[discarded]': true,
                'page[limit]': MAX_API_PAGE_SIZE,
                ...(direction && cursor ? { [`page[${direction}]`]: cursor } : {}),
                ...(startDate ? { 'filter[start_date]': startDate } : {}),
                ...(endDate ? { 'filter[end_date]': endDate } : {}),
              },
              signal,
            });

            return {
              jobs: response.data.data.map(Job.parse),
              pagination: extractPagination(response),
            };
          } catch (error) {
            connection.handleRequestError(
              error,
              t('error_messages.jobs.failed_to_fetch'),
            );
            throw error;
          }
        }
      : skipToken,
  });

  return {
    isFetching: query.isFetching,
    jobs: query.data?.jobs ?? [],
    pagination: query.data?.pagination,
  };
}
